import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import SearchableSelect from '../components/ui/SearchableSelect';
import toast from 'react-hot-toast';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';

// PostgREST returns max 1000 rows per request — page anything that can grow beyond that.
async function fetchAllPaged(buildQuery) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  return all;
}
// .in('col', ids) with hundreds of ids makes the URL too long — query in chunks of 100.
async function fetchInChunks(ids, buildQuery) {
  let out = [];
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    out = out.concat(await fetchAllPaged(() => buildQuery(chunk)));
  }
  return out;
}
// Local (IST) date. toISOString() is the UTC date = "yesterday" before 5:30am IST.
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const esc = t => String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const isOverdueSc = (sc, today) => sc.status !== 'paid' && sc.status !== 'waived' && !!sc.due_date && sc.due_date < today;
const waPhone = raw => { const ph = String(raw || '').split('/').pop().replace(/\D/g, ''); return ph.startsWith('91') ? ph : ph.length === 10 ? '91' + ph : ph; };
function openWA(raw, msg) {
  const ph = waPhone(raw);
  if (!ph) { toast.error('No phone number'); return; }
  window.open(`whatsapp://send?phone=${ph}&text=${encodeURIComponent(msg)}`, '_self');
}
function exportRows(name, rows) {
  if (!rows.length) { toast.error('Nothing to export'); return; }
  saveStyledXlsx(`${name}_${localDate()}.xlsx`, [{ name: 'Report', title: String(name).replace(/_/g, ' '), rows }]);
}
// Best-effort audit (re-uses the fee_audit_log table created for the Fees page)
async function logSchedAudit(byEmail, action, st, detail, amount) {
  try {
    await supabase.from('fee_audit_log').insert({ action, student_id: st?.id != null ? String(st.id) : null, student_name: st?.full_name || null,
      register_number: st?.register_number || null, amount: amount != null ? Number(amount) : null, detail: detail || null, by_email: byEmail || null });
  } catch (e) { /* table may be missing */ }
}
// Spread the student's real payments over their installments (oldest first). Refund / transfer-out rows are negative:
// they are taken off the most recent payments first. Writes ONE update per installment.
async function allocatePayments(studentIds) {
  if (!studentIds.length) return;
  const pays = await fetchInChunks(studentIds, ch => supabase.from('fee_payments').select('id, student_id, amount, payment_date').in('student_id', ch).order('payment_date').order('id'));
  const scheds = await fetchInChunks(studentIds, ch => supabase.from('student_fee_schedules').select('*').in('student_id', ch).order('student_id').order('installment_number'));
  for (const sid of studentIds) {
    const my = pays.filter(x => x.student_id === sid);
    let refundLeft = my.filter(x => Number(x.amount) < 0).reduce((a, x) => a + Math.abs(Number(x.amount)), 0);
    const net = my.filter(x => Number(x.amount) > 0).map(x => ({ date: x.payment_date, amt: Number(x.amount) }));
    for (let i = net.length - 1; i >= 0 && refundLeft > 0; i--) { const cut = Math.min(net[i].amt, refundLeft); net[i].amt -= cut; refundLeft -= cut; }
    const rows = scheds.filter(x => x.student_id === sid).map(x => ({ ...x, paid: 0, dates: [], amts: [] }));
    for (const pay of net) {
      let remaining = pay.amt;
      for (const sc of rows) {
        if (remaining <= 0) break;
        if (sc.status === 'waived') continue;
        const due = Number(sc.amount) - sc.paid;
        if (due <= 0) continue;
        const apply = Math.min(remaining, due);
        sc.paid += apply; sc.dates.push(pay.date); sc.amts.push(apply); remaining -= apply;
      }
    }
    const writes = rows.filter(sc => sc.status !== 'waived').map(sc => {
      const status = sc.paid <= 0 ? 'pending' : sc.paid >= Number(sc.amount) ? 'paid' : 'partial';
      return () => supabase.from('student_fee_schedules').update({
        paid_amount: sc.paid, status, paid_date: status === 'paid' ? (sc.dates[sc.dates.length - 1] || null) : null,
        payment_dates: sc.dates, payment_amounts: sc.amts,
      }).eq('id', sc.id);
    });
    for (let i = 0; i < writes.length; i += 10) {
      const res = await Promise.all(writes.slice(i, i + 10).map(w => w()));
      const bad = res.find(r => r.error);
      if (bad) throw bad.error;
    }
  }
}
let _syncRun = 0;

const ALL_TABS = [
  { key:'schedules', label:'📋 Schedules', pkey:'fee_schedules_view' },
  { key:'tracker',   label:'📊 Tracker',   pkey:'fee_schedules_tracker' },
  { key:'plans',     label:'⚙️ Fee Plans',  pkey:'fee_schedules_plans' },
  { key:'reports',   label:'📈 Reports',   pkey:'fee_schedules_reports' },
];

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return localDate(d);
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtAmt(a) {
  return '₹' + Number(a).toLocaleString('en-IN');
}

function statusBadge(status, dueDate) {
  const today = localDate();
  if (status === 'paid') return <span className="badge badge-green">✓ Paid</span>;
  if (status === 'waived') return <span className="badge badge-gray">Waived</span>;
  if (status === 'partial') return dueDate && dueDate < today ? <span className="badge badge-red">Partial · Overdue</span> : <span className="badge badge-yellow">Partial</span>;
  if (dueDate && dueDate < today) return <span className="badge badge-red">Overdue</span>;
  if (dueDate && dueDate <= addDays(today, 7)) return <span className="badge badge-yellow">Due Soon</span>;
  return <span className="badge badge-gray">Pending</span>;
}

function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  return [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
}

export default function FeeSchedules() {
  const { isEditor, canEdit, canView } = useAuth();
  const [tab, setTab] = useState('schedules');
  const { user: authUser } = useAuth();

  // Shared data
  const [batches, setBatches] = useState([]);
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);

  // Schedules tab
  const [selBatch, setSelBatch] = useState('');
  const [batchStudents, setBatchStudents] = useState([]);
  const [schedules, setSchedules] = useState([]); // student_fee_schedules
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selStudent, setSelStudent] = useState(null);
  const [editModal, setEditModal] = useState(null); // schedule row to edit
  const [extModal, setExtModal] = useState(null); // extension request
  const [extForm, setExtForm] = useState({requested_date:'',reason:''});
  const [extSaving, setExtSaving] = useState(false);
  const [revertModal, setRevertModal] = useState(null); // {req, originalDate}
  const [revertDate, setRevertDate] = useState('');
  const [extRequests, setExtRequests] = useState([]); // all extension requests

  // Tracker tab
  const [trackerBatch, setTrackerBatch] = useState('');
  const [trackerInstallment, setTrackerInstallment] = useState('');
  const [trackerData, setTrackerData] = useState([]);
  const [trackerLoading, setTrackerLoading] = useState(false);

  // Reports tab
  const [reportType, setReportType] = useState('defaulters');
  const [reminderDays, setReminderDays] = useState(15);
  const [reportBatch, setReportBatch] = useState('');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [reportData, setReportData] = useState(null);
  const [selMilestone, setSelMilestone] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [overdueDays, setOverdueDays] = useState(7);

  // Late fee / bulk shift / plan copy
  const [lateRate, setLateRate] = useState(() => { try { return localStorage.getItem('fs_late_rate') || ''; } catch (e) { return ''; } });
  const [shiftModal, setShiftModal] = useState(null); // {days, fromInst, oldStart}
  const [shifting, setShifting] = useState(false);
  const [copyModal, setCopyModal] = useState(null);   // {fromYear, toYear, pct}

  // Plans tab
  const [selPkgPlan, setSelPkgPlan] = useState('');
  const [pkgPlans, setPkgPlans] = useState([]);
  const [planForm, setPlanForm] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);

  useEffect(() => {
    // open on the first tab this user may actually see (the default 'schedules' may be hidden for them)
    const vis = ALL_TABS.filter(t => canView(t.pkey));
    if (vis.length && !vis.some(t => t.key === tab)) setTab(vis[0].key);
  }, []); // eslint-disable-line

  useEffect(() => {
    supabase.from('batches').select('id,batch_name,start_date,discipline,centre,status').order('batch_name')
      .then(({data}) => setBatches(sortBatches(data||[])));
    supabase.from('course_packages').select('id,course_name,course_short_code').order('course_name')
      .then(({data}) => setPackages(data||[]));
    supabase.from('fee_installment_plans').select('*').order('package_id').order('installment_number')
      .then(({data}) => setPlans(data||[]));
  }, []);

  // another staff member changed a schedule, payment or extension request: refresh the open batch quietly
  useLiveRefresh(['student_fee_schedules', 'fee_payments', 'fee_extension_requests'], () => {
    if (selBatch) loadBatchSchedules(selBatch, true);
    if (trackerBatch && trackerData.length) loadTracker(true);
  }, true, 2000);

  // ── SCHEDULES TAB ──────────────────────────────────────────────
  async function loadBatchSchedules(batch, silent) {
    if (!batch) return;
    if (silent !== true) setLoading(true);
    try {
    const {data: stds} = await supabase.from('students')
      .select('id,full_name,register_number,course_name,course_package_id,student_status')
      .eq('batch_name', batch).neq('is_active', false)
      .not('student_status','in','("Discontinued","Shifted")')
      .order('register_number');
    // Exclude zero-fee packages (CP, MP) — no fee schedules needed
    // Zero-fee packages (CP, MP): the two known ids, plus any package whose fee plan adds up to ₹0.
    // A package with NO plan at all stays visible (flagged) so the missing plan gets noticed.
    const ZERO_FEE_PKGS = ['602bba0f-cf9c-42be-861f-e73ed4ae9dfd','6200ce31-2e63-4e03-b662-915e54f86f8d'];
    const planTotal = {};
    plans.forEach(p => { planTotal[p.package_id] = (planTotal[p.package_id] || 0) + Number(p.amount || 0); });
    const isZeroFee = id => ZERO_FEE_PKGS.includes(id) || (id in planTotal && planTotal[id] === 0);
    const feeStudents = (stds||[]).filter(s => s.course_package_id && !isZeroFee(s.course_package_id));
    setBatchStudents(feeStudents);

    const sIds = feeStudents.map(s=>s.id);
    if (sIds.length) {
      const scheds = await fetchInChunks(sIds, ch => supabase.from('student_fee_schedules')
        .select('*').in('student_id', ch).order('student_id').order('installment_number'));
      setSchedules(scheds||[]);
      // Payments added by bulk import / older entries are not spread over the installments.
      // In the background, find students whose received money is NOT fully shown and re-allocate them.
      const myRun = ++_syncRun;
      (async () => {
        try {
          const pays = await fetchInChunks(sIds, ch => supabase.from('fee_payments')
            .select('id,student_id,amount').in('student_id', ch).order('id'));
          const net = {};
          (pays||[]).forEach(x => { net[x.student_id] = (net[x.student_id]||0) + Number(x.amount||0); });
          const alloc = {}, cap = {};
          (scheds||[]).forEach(sc => {
            alloc[sc.student_id] = (alloc[sc.student_id]||0) + Number(sc.paid_amount||0);
            if (sc.status !== 'waived') cap[sc.student_id] = (cap[sc.student_id]||0) + Number(sc.amount||0);
          });
          const stale = sIds.filter(id => cap[id] > 0 && (net[id]||0) > 0 && (alloc[id]||0) + 1 < Math.min(net[id], cap[id]));
          if (!stale.length) return;
          toast.loading && toast.loading('Updating paid amounts from payment records…', { id: 'fs-sync' });
          await allocatePayments(stale);
          const fresh = await fetchInChunks(sIds, ch => supabase.from('student_fee_schedules')
            .select('*').in('student_id', ch).order('student_id').order('installment_number'));
          if (myRun === _syncRun) setSchedules(fresh);
          toast.success(`Updated paid amounts for ${stale.length} student${stale.length>1?'s':''} from their payment records`, { id: 'fs-sync' });
        } catch (e) { toast.error('Could not sync payments: ' + e.message, { id: 'fs-sync' }); }
      })();
    } else setSchedules([]);
    } catch(e) {
      toast.error('Error loading: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function generateSchedules(forceRegenerate=false) {
    if (!selBatch) return;
    const batch = batches.find(b=>b.batch_name===selBatch);
    if (!batch?.start_date) { toast.error('Batch has no start date'); return; }

    const studentsWithoutSchedule = batchStudents.filter(s =>
      !schedules.some(sc=>sc.student_id===s.id)
    );
    if (!studentsWithoutSchedule.length && !forceRegenerate) {
      if (window.confirm('All students already have schedules.\n\nRegenerating will DELETE every schedule in this batch and rebuild it from the fee plan:\n• custom due dates and approved extensions are lost\n• waived installments become pending again\n• payments are re-allocated from the payment records\n\nClick OK only if the batch start date or the fee plan changed.')) {
        generateSchedules(true);
      }
      return;
    }
    const studentsToProcess = forceRegenerate ? batchStudents : studentsWithoutSchedule;
    if (!studentsToProcess.length) { toast.error('No students found'); return; }
    setGenerating(true);
    try {
    if (forceRegenerate) {
      const ids = batchStudents.map(s=>s.id);
      for (let i=0;i<ids.length;i+=100) {
        const {error} = await supabase.from('student_fee_schedules').delete().in('student_id', ids.slice(i,i+100));
        if (error) throw error;
      }
    }
    let generated = 0, errors = 0, noPackage = 0, noPlan = 0;
    const doneIds = [];

    // Fetch fresh plans from DB
    const {data: freshPlans} = await supabase.from('fee_installment_plans').select('*').order('package_id').order('installment_number');
    const allPlans = freshPlans || [];
    const batchYear = new Date(batch.start_date + 'T00:00:00').getFullYear();

    for (const s of studentsToProcess) {
      if (!s.course_package_id) { noPackage++; continue; }
      // Use only ONE version of the plan: the latest valid_from_year that is not after the batch year
      // (otherwise a package with a 2025 plan and a 2026 plan would get both sets of installments).
      const forPkg = allPlans.filter(p => p.package_id === s.course_package_id);
      const usable = forPkg.filter(p => Number(p.valid_from_year || 0) <= batchYear);
      const useYear = usable.length ? Math.max(...usable.map(p => Number(p.valid_from_year || 0))) : null;
      const pkgPlansForStudent = (useYear == null ? forPkg : usable.filter(p => Number(p.valid_from_year || 0) === useYear))
        .sort((a,b)=>a.installment_number-b.installment_number);
      if (!pkgPlansForStudent.length) { noPlan++; continue; }

      let dueDate = null;
      let monthlyCount = 0;
      const rows = pkgPlansForStudent.map(p => {
        if (p.type === 'registration') {
          dueDate = addDays(batch.start_date, 10);
        } else if (p.type === 'monthly') {
          monthlyCount++;
          dueDate = addDays(batch.start_date, 10 + (30 * monthlyCount));
        } else {
          dueDate = null; // exam/certificate - set manually
        }
        return {
          student_id: s.id,
          plan_id: p.id,
          installment_number: p.installment_number,
          label: p.label,
          amount: p.amount,
          due_date: dueDate,
          status: 'pending',
          paid_amount: 0,
        };
      });

      const {error} = await supabase.from('student_fee_schedules').insert(rows);
      if (error) errors++;
      else { generated++; doneIds.push(s.id); }
    }

    let msg = `Generated schedules for ${generated} students`;
    if (errors) msg += ` (${errors} errors)`;
    if (noPackage) msg += ` — ${noPackage} skipped (no course package)`;
    if (noPlan) msg += ` — ${noPlan} skipped (no fee plan for the package)`;
    toast.success(msg);
    if (noPackage) toast.error(`${noPackage} students have no course package — assign packages in Students section first`);

    // Allocate the students' real payments (incl. refunds) to the new schedules
    if (doneIds.length) {
      await allocatePayments(doneIds);
      toast.success('Existing payments allocated to the schedules');
    }
    logSchedAudit(authUser?.email, forceRegenerate ? 'schedule_regenerate' : 'schedule_generate', { full_name: selBatch }, `${generated} students, batch ${selBatch}`);
    } catch(e) {
      toast.error('Error: ' + e.message);
    } finally {
      setGenerating(false);
    }
    await loadBatchSchedules(selBatch);
  }

  async function saveExtensionRequest() {
    if (!extModal || !extForm.requested_date) { toast.error('Please select a new date'); return; }
    if (extModal.due_date && extForm.requested_date <= extModal.due_date && !window.confirm('The requested date is not later than the current due date. Submit anyway?')) return;
    setExtSaving(true);
    const {error} = await supabase.from('fee_extension_requests').insert({
      schedule_id: extModal.id,
      student_id: selStudent.id,
      original_due_date: extModal.due_date,
      requested_date: extForm.requested_date,
      reason: extForm.reason || null,
      status: 'pending',
    });
    if (error) toast.error(error.message);
    else {
      toast.success('Extension request submitted');
      setExtModal(null);
      setExtForm({requested_date:'',reason:''});
      const {data} = await supabase.from('fee_extension_requests')
        .select('*').eq('student_id', selStudent.id).order('created_at', {ascending:false});
      setExtRequests(data||[]);
    }
    setExtSaving(false);
  }

  async function approveExtension(req) {
    // Move the due date first; only then mark the request approved (so a failure cannot leave "approved" with the old date)
    const {error: dErr} = await supabase.from('student_fee_schedules')
      .update({due_date: req.requested_date})
      .eq('id', req.schedule_id);
    if (dErr) { toast.error('Could not update the due date: ' + dErr.message); return; }
    const {error} = await supabase.from('fee_extension_requests')
      .update({status:'approved', approved_date: localDate()})
      .eq('id', req.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Extension approved — due date updated');
    logSchedAudit(authUser?.email, 'extension_approved', selStudent, `${req.original_due_date||'—'} → ${req.requested_date}`);
    await loadBatchSchedules(selBatch);
    const {data: extData} = await supabase.from('fee_extension_requests')
      .select('*').eq('student_id', selStudent.id).order('created_at', {ascending:false});
    setExtRequests(extData||[]);
  }

  async function rejectExtension(req) {
    const {error} = await supabase.from('fee_extension_requests')
      .update({status:'rejected'}).eq('id', req.id);
    if (error) { toast.error(error.message); return; }
    {
      toast.success('Extension rejected');
      const {data} = await supabase.from('fee_extension_requests')
        .select('*').eq('student_id', selStudent.id).order('created_at', {ascending:false});
      setExtRequests(data||[]);
    }
  }

  function revertExtension(req) {
    setRevertModal(req);
    setRevertDate(req.original_due_date||'');
  }

  async function confirmRevert() {
    if (!revertModal || !revertDate) return;
    const {error} = await supabase.from('student_fee_schedules')
      .update({due_date: revertDate}).eq('id', revertModal.schedule_id);
    if (error) { toast.error(error.message); return; }
    const {error: nErr} = await supabase.from('fee_extension_requests')
      .update({status:'rejected', notes:'Due date changed to '+revertDate+' by staff'})
      .eq('id', revertModal.id);
    if (nErr) toast.error('Date changed, but the request note could not be saved: ' + nErr.message);
    toast.success('Due date updated to '+fmtDate(revertDate));
    logSchedAudit(authUser?.email, 'due_date_changed', selStudent, `${revertModal.requested_date||'—'} → ${revertDate}`);
    setRevertModal(null); setRevertDate('');
    await loadBatchSchedules(selBatch);
    const {data: extData} = await supabase.from('fee_extension_requests')
      .select('*').eq('student_id', selStudent.id).order('created_at', {ascending:false});
    setExtRequests(extData||[]);
  }

  async function saveScheduleRow(row) {
    const amt = Number(row.amount), paidAmt = Number(row.paid_amount)||0, late = Number(row.late_fee)||0;
    if (!(amt >= 0) || paidAmt < 0 || late < 0) { toast.error('Amounts cannot be negative'); return; }
    if (row.status === 'paid' && paidAmt < amt && !window.confirm(`Status is "paid" but the paid amount (₹${paidAmt}) is less than the amount due (₹${amt}). Save anyway?`)) return;
    if (row.status === 'partial' && (paidAmt <= 0 || paidAmt >= amt) && !window.confirm('Status "partial" does not match the paid amount. Save anyway?')) return;
    const {error} = await supabase.from('student_fee_schedules')
      .update({
        amount: amt,
        due_date: row.due_date || null,
        status: row.status,
        paid_amount: Number(row.paid_amount)||0,
        paid_date: row.paid_date || null,
        notes: row.notes || null,
        late_fee: Number(row.late_fee)||0,
      }).eq('id', row.id);
    if (error) toast.error(error.message);
    else {
      toast.success('Saved (a later payment will re-allocate this installment automatically)');
      logSchedAudit(authUser?.email, 'schedule_edit', selStudent, `${row.label}: due ${row.due_date||'—'}, amount ₹${amt}, paid ₹${paidAmt}, status ${row.status}`, amt);
      setEditModal(null);
      await loadBatchSchedules(selBatch);
    }
  }

  // ── TRACKER TAB ────────────────────────────────────────────────
  async function loadTracker(silent) {
    if (!trackerBatch) return;
    if (silent !== true) setTrackerLoading(true);
    try {
      const stds = await fetchAllPaged(() => supabase.from('students')
        .select('id,full_name,register_number,course_name').eq('batch_name',trackerBatch)
        .neq('is_active',false).not('student_status','in','("Discontinued","Shifted")').order('register_number').order('id'));
      const sIds = (stds||[]).map(s=>s.id);
      const scheds = await fetchInChunks(sIds, ch => supabase.from('student_fee_schedules')
        .select('*').in('student_id', ch).order('student_id').order('installment_number'));
      const merged = (stds||[]).map(s => ({
        ...s,
        schedules: (scheds||[]).filter(sc=>sc.student_id===s.id)
      }));
      setTrackerData(merged);
    } catch (e) { toast.error('Could not load tracker: ' + e.message); }
    setTrackerLoading(false);
  }

  // ── PLANS TAB ──────────────────────────────────────────────────
  async function loadReport() {
    if (!reportBatch) { toast.error('Select a batch first'); return; }
    setReportLoading(true);
    setReportData(null);
    const today = localDate();
    try {
      const allOngoing = reportBatch === '__ongoing__';
      const names = allOngoing ? batches.filter(b => b.status === 'ongoing').map(b => b.batch_name) : [reportBatch];
      if (!names.length) { toast.error('No ongoing batches'); setReportLoading(false); return; }
      const stds = await fetchInChunks(names, ch => supabase.from('students')
        .select('id,full_name,register_number,course_name,batch_name,contact_number,whatsapp_number,guardian_name,guardian_contact')
        .in('batch_name', ch).neq('is_active', false)
        .not('student_status','in','("Discontinued","Shifted")')
        .order('register_number').order('id'));
      const sIds = (stds||[]).map(s=>s.id);
      const scheds = await fetchInChunks(sIds, ch => supabase.from('student_fee_schedules')
        .select('*').in('student_id', ch).order('student_id').order('installment_number'));
      const payments = await fetchInChunks(sIds, ch => supabase.from('fee_payments')
        .select('student_id,amount,payment_date').in('student_id', ch)
        .order('payment_date', {ascending:false}).order('id'));

      const schedsByStudent = {};
      (scheds||[]).forEach(sc => {
        if (!schedsByStudent[sc.student_id]) schedsByStudent[sc.student_id] = [];
        schedsByStudent[sc.student_id].push(sc);
      });
      const paysByStudent = {};
      (payments||[]).forEach(p => {
        if (!paysByStudent[p.student_id]) paysByStudent[p.student_id] = [];
        paysByStudent[p.student_id].push(p);
      });

      const enriched = (stds||[]).map(s => ({
        ...s,
        schedules: schedsByStudent[s.id]||[],
        payments: paysByStudent[s.id]||[],
        totalFees: (schedsByStudent[s.id]||[]).reduce((a,sc)=>a+Number(sc.amount),0),
        totalPaid: (schedsByStudent[s.id]||[]).reduce((a,sc)=>a+Number(sc.paid_amount||0),0),
        netReceived: (paysByStudent[s.id]||[]).reduce((a,p)=>a+Number(p.amount||0),0),
      }));
      setReportData({enriched, today, batch: allOngoing ? 'All ongoing batches' : reportBatch});
    } catch (e) { toast.error('Report failed: ' + e.message); }
    setReportLoading(false);
  }

  function printReport(title, html) {
    const w = window.open('','_blank');
    if (!w) { toast.error('Allow pop-ups to print'); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>${esc(title)}</title>
      <style>body{font-family:Arial,sans-serif;font-size:11px;padding:16px}
      h2{font-size:15px;color:#1a3a6b;margin-bottom:4px}
      p{font-size:11px;color:#666;margin-bottom:10px}
      table{width:100%;border-collapse:collapse}
      th{background:#1a3a6b;color:#fff;padding:6px 8px;text-align:left;font-size:11px}
      td{border:1px solid #e5e7eb;padding:5px 8px;font-size:11px}
      tr:nth-child(even){background:#f9fafb}
      .badge-red{background:#fee2e2;color:#dc2626;padding:2px 6px;border-radius:4px;font-size:10px}
      .badge-green{background:#dcfce7;color:#15803d;padding:2px 6px;border-radius:4px;font-size:10px}
      .badge-yellow{background:#fef9c3;color:#854d0e;padding:2px 6px;border-radius:4px;font-size:10px}
      @media print{button{display:none}@page{size:A4 landscape;margin:8mm}}</style>
      </head><body>${html}<br/><button onclick="window.print()">🖨️ Print</button></body></html>`);
    w.document.close();
  }

  function loadPkgPlans(pkgId, list = plans) {
    setPkgPlans(list.filter(p=>p.package_id===pkgId).sort((a,b)=>a.installment_number-b.installment_number));
  }

  async function deletePlan(p) {
    if (!window.confirm(`Delete installment "${p.label}" from this plan?\n\nExisting student schedules are NOT changed — only schedules generated from now on.`)) return;
    const {error} = await supabase.from('fee_installment_plans').delete().eq('id', p.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Deleted');
    const {data} = await supabase.from('fee_installment_plans').select('*').order('package_id').order('installment_number');
    setPlans(data||[]);
    loadPkgPlans(selPkgPlan, data||[]);
  }

  async function savePlan(form) {
    if (!(Number(form.amount) >= 0) || form.amount === '') { toast.error('Enter a valid amount'); return; }
    if (!form.label.trim()) { toast.error('Enter a label'); return; }
    const yr = Number(form.valid_from_year) || 2025;
    if (pkgPlans.some(x => x.id !== form.id && Number(x.installment_number) === Number(form.installment_number) && Number(x.valid_from_year) === yr)) {
      toast.error(`Installment #${form.installment_number} already exists for ${yr}`); return;
    }
    setPlanSaving(true);
    const payload = { package_id: selPkgPlan, installment_number: Number(form.installment_number),
      label: form.label, amount: Number(form.amount), type: form.type, valid_from_year: Number(form.valid_from_year)||2025 };
    let error;
    if (form.id) {
      ({error} = await supabase.from('fee_installment_plans').update(payload).eq('id',form.id));
    } else {
      ({error} = await supabase.from('fee_installment_plans').insert(payload));
    }
    if (error) toast.error(error.message);
    else {
      toast.success('Saved');
      setPlanForm(null);
      const {data} = await supabase.from('fee_installment_plans').select('*').order('package_id').order('installment_number');
      setPlans(data||[]);
      loadPkgPlans(selPkgPlan, data||[]);
    }
    setPlanSaving(false);
  }

  // ── LATE FEE ───────────────────────────────────────────────────
  async function applyLateFees(list) {
    const rate = Number(lateRate) || 0;
    if (rate <= 0) { toast.error('Enter the late fee per day (₹)'); return; }
    try { localStorage.setItem('fs_late_rate', String(rate)); } catch (e) { /* ignore */ }
    const items = list.flatMap(st => st.overdueItems.map(sc => ({ sc, st })));
    if (!items.length) return;
    if (!window.confirm(`Set late fee = ₹${rate} × days overdue on ${items.length} overdue installments (${list.length} students)?\n\nThis replaces any late fee already entered on them. It is a record only — it is not added to the fee balance.`)) return;
    const t = localDate();
    let n = 0;
    for (const { sc } of items) {
      const days = Math.max(0, Math.floor((new Date(t) - new Date(sc.due_date)) / 86400000));
      const { error } = await supabase.from('student_fee_schedules').update({ late_fee: rate * days }).eq('id', sc.id);
      if (error) { toast.error(error.message); break; }
      n++;
    }
    toast.success(`Late fee set on ${n} installments`);
    logSchedAudit(authUser?.email, 'late_fee_applied', { full_name: reportData?.batch }, `₹${rate}/day on ${n} installments`);
    loadReport();
  }

  // ── BULK SHIFT DUE DATES ───────────────────────────────────────
  function shiftPreview() {
    if (!shiftModal) return [];
    const d = Number(shiftModal.days) || 0;
    const from = Number(shiftModal.fromInst) || 1;
    if (!d) return [];
    return schedules.filter(sc => sc.status !== 'paid' && sc.status !== 'waived' && sc.due_date && Number(sc.installment_number) >= from);
  }
  async function runShift() {
    const rows = shiftPreview();
    const d = Number(shiftModal?.days) || 0;
    if (!rows.length || !d) { toast.error('Nothing to shift'); return; }
    if (!window.confirm(`Move ${rows.length} unpaid due dates by ${d > 0 ? '+' : ''}${d} days in batch ${selBatch}?`)) return;
    setShifting(true);
    try {
      for (let i = 0; i < rows.length; i += 20) {
        const res = await Promise.all(rows.slice(i, i + 20).map(sc =>
          supabase.from('student_fee_schedules').update({ due_date: addDays(sc.due_date, d) }).eq('id', sc.id)));
        const bad = res.find(r => r.error);
        if (bad) throw bad.error;
      }
      toast.success(`${rows.length} due dates moved`);
      logSchedAudit(authUser?.email, 'due_dates_shifted', { full_name: selBatch }, `${d > 0 ? '+' : ''}${d} days on ${rows.length} installments from #${shiftModal.fromInst || 1}`);
      setShiftModal(null);
      await loadBatchSchedules(selBatch);
    } catch (e) { toast.error('Shift failed (some dates may have moved): ' + e.message); await loadBatchSchedules(selBatch); }
    setShifting(false);
  }

  // ── COPY PLAN TO A NEW YEAR ────────────────────────────────────
  async function runCopyPlan() {
    const { fromYear, toYear, pct } = copyModal;
    const src = pkgPlans.filter(p => Number(p.valid_from_year) === Number(fromYear));
    const ty = Number(toYear);
    if (!src.length) { toast.error('No installments for the source year'); return; }
    if (!ty || ty < 2000 || ty > 2100) { toast.error('Enter a valid target year'); return; }
    if (pkgPlans.some(p => Number(p.valid_from_year) === ty)) { toast.error(`A ${ty} plan already exists for this package`); return; }
    const f = 1 + (Number(pct) || 0) / 100;
    const rows = src.map(p => ({ package_id: p.package_id, installment_number: p.installment_number, label: p.label,
      amount: Math.round(Number(p.amount) * f), type: p.type, valid_from_year: ty }));
    const { error } = await supabase.from('fee_installment_plans').insert(rows);
    if (error) { toast.error(error.message); return; }
    toast.success(`${rows.length} installments copied to ${ty}`);
    logSchedAudit(authUser?.email, 'plan_copied', { full_name: packages.find(x => x.id === selPkgPlan)?.course_name }, `${fromYear} → ${ty} (${Number(pct) || 0}%)`);
    setCopyModal(null);
    const { data } = await supabase.from('fee_installment_plans').select('*').order('package_id').order('installment_number');
    setPlans(data || []);
    loadPkgPlans(selPkgPlan, data || []);
  }

  // ── STUDENT DETAIL ─────────────────────────────────────────────
  const planPkgIds = new Set(plans.map(p => p.package_id));
  const studentSchedules = selStudent ? schedules.filter(s=>s.student_id===selStudent.id) : [];
  const totalFees = studentSchedules.reduce((a,s)=>a+Number(s.amount),0);
  const totalPaid = studentSchedules.reduce((a,s)=>a+Number(s.paid_amount||0),0);
  const balance = totalFees - totalPaid;

  return (
    <div>
      <div className="tabs" style={{marginBottom:16}}>
        {ALL_TABS.filter(t=>canView(t.pkey)).map(t=>(
          <div key={t.key} className={`tab ${tab===t.key?'active':''}`} onClick={()=>setTab(t.key)}>{t.label}</div>
        ))}
      </div>

      {/* ── SCHEDULES TAB ── */}
      {tab==='schedules' && (
        <div>
          <div className="card" style={{marginBottom:16,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <SearchableSelect value={selBatch} onChange={v=>{setSelBatch(v||'');setBatchStudents([]);setSchedules([]);setSelStudent(null);if(v)loadBatchSchedules(v);}}
              options={batches.map(b=>({value:b.batch_name,label:b.batch_name+' ('+b.status+')'}))}
              placeholder="Select Batch" searchPlaceholder="Search batch..." style={{minWidth:220}}/>
            {selBatch && batchStudents.length>0 && batches.find(b=>b.batch_name===selBatch)?.status==='ongoing' && (
              <button className="btn btn-primary btn-sm" onClick={()=>generateSchedules(false)} disabled={generating}>
                {generating?'Generating...':'⚡ Generate Schedules'}
              </button>
            )}
            {selBatch && batchStudents.length>0 && batches.find(b=>b.batch_name===selBatch)?.status!=='ongoing' && (
              <span style={{fontSize:11,color:'var(--text3)',padding:'4px 10px',background:'var(--surface2)',borderRadius:6}}>
                🔒 Schedule generation only for ongoing batches
              </span>
            )}
            {selBatch && batchStudents.length>0 && canEdit('fee_schedules') && (
              <button className="btn btn-ghost btn-sm" onClick={()=>setShiftModal({days:'',fromInst:1,oldStart:''})}>📅 Shift due dates</button>
            )}
            {selBatch && <span style={{fontSize:12,color:'var(--text3)'}}>
              {batchStudents.length} students | {batchStudents.filter(s=>schedules.some(sc=>sc.student_id===s.id)).length} with schedules
            </span>}
          </div>

          {loading && <div className="card"><p>Loading...</p></div>}

          {!loading && selBatch && batchStudents.length>0 && (
            <div style={{display:'grid',gridTemplateColumns:selStudent?'300px 1fr':'1fr',gap:16}}>
              {/* Student list */}
              <div className="card" style={{padding:0,overflowY:'auto',maxHeight:'75vh'}}>
                <div style={{padding:'10px 12px',fontWeight:600,fontSize:13,borderBottom:'1px solid var(--border)',background:'var(--accent-light)'}}>
                  Students
                </div>
                {batchStudents.map(s=>{
                  const sScheds = schedules.filter(sc=>sc.student_id===s.id);
                  const paid = sScheds.reduce((a,sc)=>a+Number(sc.paid_amount||0),0);
                  const total = sScheds.reduce((a,sc)=>a+Number(sc.amount),0);
                  const overdue = sScheds.filter(sc=>isOverdueSc(sc,localDate())).length;
                  return (
                    <div key={s.id} onClick={async()=>{
                      if (selStudent?.id===s.id) { setSelStudent(null); setExtRequests([]); return; }
                      setSelStudent(s);
                      const {data} = await supabase.from('fee_extension_requests')
                        .select('*').eq('student_id', s.id).order('created_at', {ascending:false});
                      setExtRequests(data||[]);
                    }}
                      style={{padding:'8px 12px',borderBottom:'1px solid var(--border)',cursor:'pointer',
                        background:selStudent?.id===s.id?'var(--accent-light)':''}}>
                      <div style={{fontWeight:500,fontSize:13}}>{s.full_name}</div>
                      <div style={{fontSize:11,color:'var(--text3)'}}>{s.register_number} | {s.course_name||'No package'}</div>
                      {sScheds.length>0 && (
                        <div style={{fontSize:11,marginTop:2,display:'flex',gap:8}}>
                          <span style={{color:'var(--green)'}}>₹{Number(paid).toLocaleString('en-IN')} paid</span>
                          {total>paid && <span style={{color:'var(--text3)'}}>/ ₹{Number(total).toLocaleString('en-IN')}</span>}
                          {overdue>0 && <span style={{color:'var(--red)',fontWeight:600}}>{overdue} overdue</span>}
                        </div>
                      )}
                      {!sScheds.length && <div style={{fontSize:11,color:'var(--amber)'}}>{planPkgIds.has(s.course_package_id) ? 'No schedule generated' : '⚠ No fee plan for this package'}</div>}
                    </div>
                  );
                })}
              </div>

              {/* Student detail */}
              {selStudent && (
                <div>
                  <div className="card" style={{marginBottom:12}}>
                    <div style={{fontWeight:700,fontSize:15,marginBottom:4}}>{selStudent.full_name}</div>
                    <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>{selStudent.register_number} | {selStudent.course_name}</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
                      {[['Total Fees',fmtAmt(totalFees),'var(--text)'],
                        ['Total Paid',fmtAmt(totalPaid),'var(--green)'],
                        ['Balance',fmtAmt(balance),balance>0?'var(--red)':'var(--green)']
                      ].map(([l,v,c])=>(
                        <div key={l} style={{textAlign:'center',padding:8,background:'var(--surface2)',borderRadius:8}}>
                          <div style={{fontSize:11,color:'var(--text3)'}}>{l}</div>
                          <div style={{fontSize:16,fontWeight:700,color:c}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="card" style={{padding:0}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                      <thead>
                        <tr style={{background:'var(--accent-light)'}}>
                          {['#','Installment','Amount','Due Date','Paid','Paid Date','Status',''].map(h=>(
                            <th key={h} style={{padding:'8px 10px',textAlign:h==='Amount'||h==='Paid'?'right':'left'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {studentSchedules.map((sc,i)=>(
                          <tr key={sc.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                            <td style={{padding:'8px 10px',color:'var(--text3)'}}>{sc.installment_number}</td>
                            <td style={{padding:'8px 10px',fontWeight:500}}>{sc.label}</td>
                            <td style={{padding:'8px 10px',textAlign:'right'}}>{fmtAmt(sc.amount)}</td>
                            <td style={{padding:'8px 10px'}}>
                              {sc.due_date ? fmtDate(sc.due_date) : <span style={{color:'var(--amber)',fontSize:11}}>Not set</span>}
                            </td>
                            <td style={{padding:'8px 10px',textAlign:'right',color:'var(--green)',fontWeight:500}}>
                              {sc.paid_amount>0?fmtAmt(sc.paid_amount):'—'}
                            </td>
                            <td style={{padding:'8px 10px',fontSize:11,color:'var(--text3)'}}>
                              {sc.payment_dates?.length>0?(
                                <div>{sc.payment_dates.map((d,i)=>(
                                  <div key={i} style={{whiteSpace:'nowrap'}}>
                                    {fmtDate(d)}
                                    {sc.payment_amounts?.[i]!=null && (
                                      <span style={{marginLeft:4,color:'var(--green)',fontWeight:600}}>
                                        {fmtAmt(sc.payment_amounts[i])}
                                      </span>
                                    )}
                                  </div>
                                ))}</div>
                              ):sc.paid_date?fmtDate(sc.paid_date):'—'}
                            </td>
                            <td style={{padding:'8px 10px'}}>{statusBadge(sc.status,sc.due_date)}
                              {Number(sc.late_fee)>0 && <div style={{fontSize:10,color:'var(--red)',marginTop:2}}>+ {fmtAmt(sc.late_fee)} late fee</div>}</td>
                            <td style={{padding:'8px 10px',display:'flex',gap:4}}>
                              {canEdit('fee_schedules') && <button className="btn btn-ghost btn-sm" onClick={()=>setEditModal({...sc})}>Edit</button>}
                              {sc.status!=='paid' && sc.status!=='waived' && sc.due_date && (()=>{
                                const approvedExt = extRequests.find(r=>r.schedule_id===sc.id&&r.status==='approved');
                                const pendingExt = extRequests.find(r=>r.schedule_id===sc.id&&r.status==='pending');
                                if (approvedExt) return (
                                  <div style={{display:'flex',gap:4}}>
                                    <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)'}}
                                      onClick={()=>revertExtension(approvedExt)}>↩ Revert</button>
                                    <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--amber)'}}
                                      onClick={()=>{setExtModal(sc);setExtForm({requested_date:'',reason:''});}}>
                                      🗓 Extend Again
                                    </button>
                                  </div>
                                );
                                if (pendingExt) return (
                                  <span style={{fontSize:10,color:'var(--amber)'}}>🕐 Pending approval</span>
                                );
                                return (
                                  <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--amber)'}}
                                    onClick={()=>{setExtModal(sc);setExtForm({requested_date:'',reason:''});}}>
                                    🗓 Extend
                                </button>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Extension Requests */}
              {extRequests.length > 0 && (
              <div className="card" style={{marginTop:12}}>
                <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>🗓 Extension History ({extRequests.length})</div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'var(--accent-light)'}}>
                      {['Installment','Original Date','Requested Date','Reason','Status','Action'].map(h=>(
                        <th key={h} style={{padding:'6px 10px',textAlign:'left'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {extRequests.map((req,i)=>{
                      const sc = studentSchedules.find(s=>s.id===req.schedule_id);
                      return (
                        <tr key={req.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                          <td style={{padding:'6px 10px',fontWeight:500}}>{sc?.label||'—'}</td>
                          <td style={{padding:'6px 10px'}}>{req.original_due_date?fmtDate(req.original_due_date):'—'}</td>
                          <td style={{padding:'6px 10px',color:'var(--amber)',fontWeight:500}}>{fmtDate(req.requested_date)}</td>
                          <td style={{padding:'6px 10px',color:'var(--text3)',fontSize:11}}>{req.reason||'—'}</td>
                          <td style={{padding:'6px 10px'}}>
                            <span className={`badge ${req.status==='approved'?'badge-green':req.status==='rejected'?'badge-red':'badge-yellow'}`}>{req.status}</span>
                          </td>
                          <td style={{padding:'6px 10px',display:'flex',gap:4}}>
                            {canEdit('fee_schedules') && req.status==='pending' && (<>
                              <button className="btn btn-primary btn-sm" style={{fontSize:10}} onClick={()=>approveExtension(req)}>✓ Approve</button>
                              <button className="btn btn-danger btn-sm" style={{fontSize:10}} onClick={()=>rejectExtension(req)}>✕ Reject</button>
                            </>)}
                            {canEdit('fee_schedules') && req.status==='approved' && (
                              <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)'}} onClick={()=>revertExtension(req)}>↩ Revert</button>
                            )}
                            {req.status==='rejected' && (
                              <span style={{fontSize:10,color:'var(--text3)'}}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          )}
        </div>
      )}

      {/* ── TRACKER TAB ── */}
      {tab==='tracker' && (
        <div>
          <div className="card" style={{marginBottom:16,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <SearchableSelect value={trackerBatch} onChange={v=>{setTrackerBatch(v||'');setTrackerData([]);}}
              options={batches.map(b=>({value:b.batch_name,label:b.batch_name+' ('+b.status+')'}))}
              placeholder="Select Batch" searchPlaceholder="Search batch..." style={{minWidth:220}}/>
            <button className="btn btn-primary btn-sm" onClick={loadTracker} disabled={!trackerBatch||trackerLoading}>
              {trackerLoading?'Loading...':'📊 Load'}
            </button>
          </div>

          {trackerData.length>0 && (()=>{
            // Get max installment number across all students
            const maxInstNum = Math.max(...trackerData.flatMap(s=>s.schedules.map(sc=>sc.installment_number)), 0);
            const allInstNums = Array.from({length:maxInstNum},(_,i)=>i+1);
            const today = localDate();

            // Summary stats
            const totalStudents = trackerData.length;
            const withSchedule = trackerData.filter(s=>s.schedules.length>0).length;
            const overdue = trackerData.filter(s=>s.schedules.some(sc=>isOverdueSc(sc,today))).length;

            return (
              <div className="card" style={{overflow:'hidden'}}><div style={{overflowX:'auto',overflowY:'auto',maxHeight:'68vh'}}>
                <div style={{display:'flex',gap:16,marginBottom:12,flexWrap:'wrap'}}>
                  <div style={{fontWeight:700,fontSize:14}}>{trackerBatch} — Fee Tracker</div>
                  <span style={{fontSize:12,background:'var(--surface2)',padding:'2px 8px',borderRadius:4}}>{withSchedule}/{totalStudents} with schedules</span>
                  {overdue>0 && <span style={{fontSize:12,background:'#fee2e2',color:'var(--red)',padding:'2px 8px',borderRadius:4,fontWeight:600}}>{overdue} with overdue</span>}
                </div>
                <table style={{width:'100%',borderCollapse:'separate',borderSpacing:0,fontSize:12}}>
                  <thead style={{position:'sticky',top:0,zIndex:10,background:'#e8f4fd'}}>
                    <tr>
                      <th style={{padding:'6px 8px',background:'#e8f4fd',position:'sticky',top:0,textAlign:'left',minWidth:40,boxShadow:'inset 0 -2px 0 #93c5fd'}}>#</th>
                      <th style={{padding:'6px 8px',background:'#e8f4fd',position:'sticky',top:0,textAlign:'left',minWidth:80,boxShadow:'inset 0 -2px 0 #93c5fd'}}>Reg No</th>
                      <th style={{padding:'6px 8px',background:'#e8f4fd',position:'sticky',top:0,textAlign:'left',minWidth:140,boxShadow:'inset 0 -2px 0 #93c5fd'}}>Name</th>
                      <th style={{padding:'6px 8px',background:'#e8f4fd',position:'sticky',top:0,textAlign:'left',minWidth:100,boxShadow:'inset 0 -2px 0 #93c5fd'}}>Package</th>
                      {allInstNums.map(n=>(
                        <th key={n} style={{padding:'4px 6px',background:'#e8f4fd',position:'sticky',top:0,textAlign:'center',minWidth:80,fontSize:10,whiteSpace:'nowrap',boxShadow:'inset 0 -2px 0 #93c5fd'}}>
                          Inst {n}
                        </th>
                      ))}
                      <th style={{padding:'6px 8px',background:'#e8f4fd',position:'sticky',top:0,textAlign:'right',minWidth:80,boxShadow:'inset 0 -2px 0 #93c5fd'}}>Total Paid</th>
                      <th style={{padding:'6px 8px',background:'#e8f4fd',position:'sticky',top:0,textAlign:'right',minWidth:80,boxShadow:'inset 0 -2px 0 #93c5fd'}}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackerData.map((s,i)=>{
                      const totalAmt = s.schedules.reduce((a,sc)=>a+Number(sc.amount),0);
                      const paidAmt = s.schedules.reduce((a,sc)=>a+Number(sc.paid_amount||0),0);
                      return (
                        <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                          <td style={{padding:'5px 8px',color:'var(--text3)'}}>{i+1}</td>
                          <td style={{padding:'5px 8px',fontFamily:'monospace',fontSize:11}}>{s.register_number}</td>
                          <td style={{padding:'5px 8px',fontWeight:500}}>{s.full_name}</td>
                          <td style={{padding:'5px 8px',fontSize:11,color:'var(--text3)'}}>{s.course_name}</td>
                          {allInstNums.map(n=>{
                            const sc = s.schedules.find(x=>x.installment_number===n);
                            if (!sc) return <td key={n} style={{textAlign:'center',padding:'5px 6px',color:'#9ca3af'}}>—</td>;
                            if (sc.status==='waived') return <td key={n} style={{textAlign:'center',padding:'5px 6px',background:'#f3f4f6',color:'#6b7280',fontSize:11}}>Waived</td>;
                            const bg = sc.status==='paid'?'#dcfce7':isOverdueSc(sc,today)?'#fee2e2':sc.status==='partial'?'#fef9c3':'';
                            const col = sc.status==='paid'?'#15803d':isOverdueSc(sc,today)?'#dc2626':sc.status==='partial'?'#854d0e':'var(--text)';
                            return (
                              <td key={n} style={{textAlign:'center',padding:'5px 6px',background:bg,color:col,fontSize:11}}>
                                {sc.status==='paid'?'✓ '+fmtAmt(sc.paid_amount):
                                  sc.status==='partial'?'~'+fmtAmt(sc.paid_amount):
                                  sc.due_date?fmtDate(sc.due_date):'Not set'}
                              </td>
                            );
                          })}
                          <td style={{padding:'5px 8px',textAlign:'right',color:'var(--green)',fontWeight:600}}>{fmtAmt(paidAmt)}</td>
                          <td style={{padding:'5px 8px',textAlign:'right',color:totalAmt-paidAmt>0?'var(--red)':'var(--green)',fontWeight:600}}>{fmtAmt(totalAmt-paidAmt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div></div>
            );
          })()}
        </div>
      )}

      {/* ── PLANS TAB ── */}
      {tab==='plans' && (
        <div>
          <div className="card" style={{marginBottom:16,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <SearchableSelect value={selPkgPlan} onChange={v=>{setSelPkgPlan(v||'');setPlanForm(null);if(v)loadPkgPlans(v);}}
              options={packages.map(p=>({value:p.id,label:p.course_short_code+' — '+p.course_name}))}
              placeholder="Select Package" searchPlaceholder="Search package..." style={{minWidth:280}}/>
            {selPkgPlan && canEdit('fee_schedules') && pkgPlans.length>0 && (
              <button className="btn btn-ghost btn-sm" onClick={()=>{
                const yrs=[...new Set(pkgPlans.map(p=>Number(p.valid_from_year)))].sort();
                setCopyModal({fromYear:yrs[yrs.length-1],toYear:yrs[yrs.length-1]+1,pct:''});
              }}>⧉ Copy plan to new year</button>
            )}
            {selPkgPlan && canEdit('fee_schedules') && (
              <button className="btn btn-primary btn-sm" onClick={()=>setPlanForm({
                package_id:selPkgPlan, installment_number:pkgPlans.length+1,
                label:'Installment '+(pkgPlans.length+1), amount:'', type:'monthly', valid_from_year:2025
              })}>+ Add Installment</button>
            )}
          </div>

          {selPkgPlan && (
            <div className="card">
              <div style={{fontWeight:600,fontSize:14,marginBottom:12}}>
                {packages.find(p=>p.id===selPkgPlan)?.course_short_code} — Installment Plan
                <span style={{fontWeight:400,fontSize:12,color:'var(--text3)',marginLeft:8}}>
                  {[...new Set(pkgPlans.map(p=>p.valid_from_year))].sort().map(y=>(
                    <span key={y} style={{marginRight:12}}>{y}: {fmtAmt(pkgPlans.filter(p=>p.valid_from_year===y).reduce((a,p)=>a+Number(p.amount),0))}</span>
                  ))}
                </span>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{background:'var(--accent-light)'}}>
                    {['#','Label','Amount','Type','Year',''].map(h=>(
                      <th key={h} style={{padding:'8px 10px',textAlign:h==='Amount'?'right':'left'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pkgPlans.map((p,i)=>(
                    <tr key={p.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                      <td style={{padding:'8px 10px',color:'var(--text3)'}}>{p.installment_number}</td>
                      <td style={{padding:'8px 10px',fontWeight:500}}>{p.label}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontWeight:600}}>{fmtAmt(p.amount)}</td>
                      <td style={{padding:'8px 10px'}}>
                        <span className={`badge ${p.type==='registration'?'badge-blue':p.type==='monthly'?'badge-green':p.type==='exam'?'badge-yellow':'badge-gray'}`}>
                          {p.type}
                        </span>
                      </td>
                      <td style={{padding:'8px 10px',color:'var(--text3)'}}>{p.valid_from_year}</td>
                      <td style={{padding:'8px 10px'}}>
                        {canEdit('fee_schedules') && <button className="btn btn-ghost btn-sm" onClick={()=>setPlanForm({...p})}>Edit</button>}
                        {canEdit('fee_schedules') && <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>deletePlan(p)}>Delete</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {/* ── REPORTS TAB ── */}
      <style>{`
        .report-table-wrap { overflow-x: auto; overflow-y: auto; max-height: 65vh; }
        .report-table-wrap table { border-collapse: separate !important; border-spacing: 0; }
        .report-table-wrap thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background: #dbeafe !important;
          border-bottom: 2px solid #3b82f6 !important;
        }
        .tracker-table-wrap { overflow-x: auto; overflow-y: auto; max-height: 70vh; }
        .tracker-table-wrap table { border-collapse: separate !important; border-spacing: 0; }
        .tracker-table-wrap thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background: #e8f4fd !important;
          border-bottom: 2px solid #93c5fd !important;
        }
      `}</style>
      {tab==='reports' && (
        <div>
          {/* Controls */}
          <div className="card" style={{marginBottom:16}}>
            <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end'}}>
              <div>
                <div style={{fontSize:11,fontWeight:600,marginBottom:4,color:'var(--text3)'}}>Report Type</div>
                <select value={reportType} onChange={e=>{setReportType(e.target.value);setReportData(null);}} style={{minWidth:200,padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)'}}>
                  <option value="defaulters">⚠️ Defaulter List</option>
                  <option value="reminders">🔔 Reminder List (Due in N days)</option>
                  <option value="installment_due">📋 Installment Due Report</option>
                  <option value="collection">💰 Collection Report</option>
                  <option value="pending_summary">📊 Fee Pending Summary</option>
                  <option value="student_schedule">👤 Student Schedule Print</option>
                  <option value="batch_tracking">📦 Batch Fee Tracking</option>
                  <option value="milestone">&#127942; Payment Milestone Report</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:600,marginBottom:4,color:'var(--text3)'}}>Batch</div>
                <SearchableSelect value={reportBatch} onChange={v=>{setReportBatch(v||'');setReportData(null);}}
                  options={[{value:'__ongoing__',label:'★ All ongoing batches'},...batches.map(b=>({value:b.batch_name,label:b.batch_name}))]}
                  placeholder="Select Batch" searchPlaceholder="Search..." style={{minWidth:160}}/>
              </div>
              {(reportType==='collection') && (<>
                <div>
                  <div style={{fontSize:11,fontWeight:600,marginBottom:4,color:'var(--text3)'}}>From</div>
                  <DateInput value={reportDateFrom} onChange={e=>setReportDateFrom(e.target.value)}/>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:600,marginBottom:4,color:'var(--text3)'}}>To</div>
                  <DateInput value={reportDateTo} onChange={e=>setReportDateTo(e.target.value)}/>
                </div>
              </>)}
              {reportType==='defaulters' && (
                <div>
                  <div style={{fontSize:11,fontWeight:600,marginBottom:4,color:'var(--text3)'}}>Overdue by (days)</div>
                  <input type="number" value={overdueDays} onChange={e=>setOverdueDays(Number(e.target.value))} style={{width:80}} min={1}/>
                </div>
              )}
              <button className="btn btn-primary" onClick={loadReport} disabled={reportLoading||!reportBatch}>
                {reportLoading?'Loading...':'📊 Generate'}
              </button>
              {reportData && <button className="btn btn-ghost btn-sm" style={{marginLeft:8}} onClick={()=>setReportData(null)}>✕ Clear</button>}
            </div>
          </div>

          {/* Report Output */}
          {reportData && (()=>{
            const {enriched, today, batch} = reportData;

            // ── DEFAULTER LIST ──
            if (reportType==='defaulters') {
              const cutoff = localDate(new Date(Date.now() - overdueDays*86400000));
              const defaulters = enriched.map(s => ({
                ...s,
                overdueItems: s.schedules.filter(sc=>sc.status!=='paid'&&sc.status!=='waived'&&sc.due_date&&sc.due_date<cutoff)
              })).filter(s=>s.overdueItems.length>0).sort((a,b)=>
                b.overdueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0) -
                a.overdueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0)
              );
              return (
                <div className="card">
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:700,fontSize:14}}>⚠️ Defaulter List — {batch}
                      <span style={{fontWeight:400,fontSize:12,color:'var(--text3)',marginLeft:8}}>Overdue &gt; {overdueDays} days | {defaulters.length} students</span>
                    </div>
                    {canEdit('fee_schedules') && (
                      <span style={{display:'flex',gap:4,alignItems:'center',fontSize:11}}>
                        Late fee ₹<input type="number" min="0" value={lateRate} onChange={e=>setLateRate(e.target.value)} style={{width:60,fontSize:12,padding:'2px 4px'}}/>/day
                        <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>applyLateFees(defaulters)}>Apply to listed</button>
                      </span>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={()=>exportRows('Defaulters_'+batch, defaulters.map((s,i)=>({
                      'S.No':i+1,'Batch':s.batch_name,'Reg No':s.register_number,'Name':s.full_name,'Course':s.course_name,
                      'Overdue Installments':s.overdueItems.map(sc=>sc.label+' ('+sc.due_date+')').join('; '),
                      'Overdue Amount':s.overdueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0),
                      'Late Fee':s.overdueItems.reduce((x,sc)=>x+Number(sc.late_fee||0),0),
                      'Contact':s.contact_number||'','Guardian':s.guardian_name||'','Guardian Contact':s.guardian_contact||''})))}>⬇ Excel</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{
                      const rows = defaulters.map((s,i)=>`<tr>
                        <td>${i+1}</td><td>${esc(s.register_number)}</td><td>${esc(s.full_name)}</td><td>${esc(s.course_name)}</td>
                        <td>${s.overdueItems.map(sc=>sc.label+' ('+fmtDate(sc.due_date)+')').join('<br/>')}</td>
                        <td style="text-align:right;color:#dc2626;font-weight:700">₹${s.overdueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0).toLocaleString('en-IN')}</td>
                        <td>${esc(s.contact_number||'—')}</td><td>${esc(s.guardian_name||'—')}</td><td>${esc(s.guardian_contact||'—')}</td>
                      </tr>`).join('');
                      printReport('Defaulter List - '+batch,
                        `<h2>⚠️ Defaulter List — ${esc(batch)}</h2><p>Overdue > ${overdueDays} days | Generated: ${fmtDate(today)}</p>
                        <table><thead><tr><th>#</th><th>Reg No</th><th>Name</th><th>Course</th><th>Overdue Installments</th><th>Overdue Amt</th><th>Contact</th><th>Guardian</th><th>Guardian Contact</th></tr></thead>
                        <tbody>${rows}</tbody></table>`);
                    }}>🖨️ Print</button>
                  </div>
                  <div className="report-table-wrap"><table style={{width:'100%',fontSize:12}}>
                    <thead><tr>
                      {['#','Reg No','Name','Course','Overdue Installments','Overdue Amt','Contact','Guardian','Guardian Contact'].map(h=>(
                        <th key={h} style={{padding:'6px 10px',textAlign:'left'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {defaulters.map((s,i)=>(
                        <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:'#fef2f2'}}>
                          <td style={{padding:'6px 10px',color:'var(--text3)'}}>{i+1}</td>
                          <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11}}>{s.register_number}</td>
                          <td style={{padding:'6px 10px',fontWeight:500}}>{s.full_name}<div style={{fontSize:10,color:'var(--text3)',fontWeight:400}}>{s.batch_name}</div></td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.course_name}</td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.overdueItems.map((sc,j)=>(
                            <div key={j}><span className="badge badge-red">{sc.label}</span> {fmtDate(sc.due_date)}</div>
                          ))}</td>
                          <td style={{padding:'6px 10px',color:'var(--red)',fontWeight:700}}>
                            {fmtAmt(s.overdueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0))}
                            {s.overdueItems.reduce((x,sc)=>x+Number(sc.late_fee||0),0)>0 && <div style={{fontSize:10,fontWeight:500}}>+ {fmtAmt(s.overdueItems.reduce((x,sc)=>x+Number(sc.late_fee||0),0))} late fee</div>}
                          </td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.contact_number||'—'}</td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.guardian_name||'—'}</td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.guardian_contact||'—'}
                            <div style={{display:'flex',gap:4,marginTop:3}}>
                              <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'#25d366',padding:'0 4px'}} onClick={()=>openWA(s.whatsapp_number||s.contact_number,`Dear ${s.full_name},\nThis is a reminder from Armstech Engineers: ${s.overdueItems.map(sc=>sc.label+' (due '+sc.due_date+')').join(', ')} is overdue. Pending amount: ₹${s.overdueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0).toLocaleString('en-IN')}. Kindly pay at the earliest. Ignore if already paid.`)}>💬 Student</button>
                              {s.guardian_contact && <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'#128c7e',padding:'0 4px'}} onClick={()=>openWA(s.guardian_contact,`Dear Mr/Ms ${s.guardian_name||'Guardian'},\nThis is a reminder from Armstech Engineers regarding ${s.full_name}: ${s.overdueItems.map(sc=>sc.label+' (due '+sc.due_date+')').join(', ')} is overdue. Pending amount: ₹${s.overdueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0).toLocaleString('en-IN')}. Kindly pay at the earliest. Ignore if already paid.`)}>💬 Guardian</button>}
                            </div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                </div>
              );
            }

            // ── REMINDER LIST ──
            if (reportType==='reminders') {
              const in15 = localDate(new Date(Date.now()+reminderDays*86400000));
              const reminders = enriched.map(s=>({
                ...s,
                dueItems: s.schedules.filter(sc=>sc.status!=='paid'&&sc.status!=='waived'&&sc.due_date&&sc.due_date>=today&&sc.due_date<=in15)
              })).filter(s=>s.dueItems.length>0).sort((a,b)=>a.dueItems[0].due_date.localeCompare(b.dueItems[0].due_date));
              return (
                <div className="card" style={{overflowX:'auto'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:700,fontSize:14,display:'flex',alignItems:'center',gap:10}}>🔔 Reminder List — {batch}
                      <span style={{fontWeight:400,fontSize:12,color:'var(--text3)'}}>Due in</span>
                      <select value={reminderDays} onChange={e=>setReminderDays(Number(e.target.value))} style={{fontSize:12,padding:'2px 6px',border:'1px solid var(--border)',borderRadius:4}}>
                        {[7,10,15,20,30,45,60].map(d=><option key={d} value={d}>{d} days</option>)}
                      </select>
                      <span style={{fontWeight:400,fontSize:12,color:'var(--text3)',marginLeft:8}}>{reminders.length} students</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>exportRows('Reminders_'+batch, reminders.map((s,i)=>({
                      'S.No':i+1,'Batch':s.batch_name,'Reg No':s.register_number,'Name':s.full_name,'Course':s.course_name,
                      'Due Installments':s.dueItems.map(sc=>sc.label+' ('+sc.due_date+')').join('; '),
                      'Amount Due':s.dueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0),
                      'Contact':s.contact_number||'','Guardian Contact':s.guardian_contact||''})))}>⬇ Excel</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{
                      const rows = reminders.map((s,i)=>`<tr>
                        <td>${i+1}</td><td>${esc(s.register_number)}</td><td>${esc(s.full_name)}</td><td>${esc(s.course_name)}</td>
                        <td>${s.dueItems.map(sc=>sc.label+' — '+fmtDate(sc.due_date)).join('<br/>')}</td>
                        <td style="text-align:right">₹${s.dueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0).toLocaleString('en-IN')}</td>
                        <td>${esc(s.contact_number||'—')}</td><td>${esc(s.guardian_contact||'—')}</td>
                      </tr>`).join('');
                      printReport('Reminder List - '+batch,
                        `<h2>🔔 Reminder List — ${esc(batch)}</h2><p>Due in ${reminderDays} days | Generated: ${fmtDate(today)}</p>
                        <table><thead><tr><th>#</th><th>Reg No</th><th>Name</th><th>Course</th><th>Due Installments</th><th>Amount Due</th><th>Contact</th><th>Guardian Contact</th></tr></thead>
                        <tbody>${rows}</tbody></table>`);
                    }}>🖨️ Print</button>
                  </div>
                  <div className="report-table-wrap"><table style={{width:'100%',fontSize:12}}>
                    <thead><tr>
                      {['#','Reg No','Name','Course','Due Installments','Amount Due','Contact','Guardian Contact'].map(h=>(
                        <th key={h} style={{padding:'6px 10px',textAlign:'left'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {reminders.map((s,i)=>(
                        <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:'#fffbeb'}}>
                          <td style={{padding:'6px 10px',color:'var(--text3)'}}>{i+1}</td>
                          <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11}}>{s.register_number}</td>
                          <td style={{padding:'6px 10px',fontWeight:500}}>{s.full_name}<div style={{fontSize:10,color:'var(--text3)',fontWeight:400}}>{s.batch_name}</div></td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.course_name}</td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.dueItems.map((sc,j)=>(
                            <div key={j}><span className="badge badge-yellow">{sc.label}</span> {fmtDate(sc.due_date)}</div>
                          ))}</td>
                          <td style={{padding:'6px 10px',color:'var(--amber)',fontWeight:700}}>
                            {fmtAmt(s.dueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0))}
                          </td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.contact_number||'—'}</td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.guardian_contact||'—'}
                            <div style={{display:'flex',gap:4,marginTop:3}}>
                              <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'#25d366',padding:'0 4px'}} onClick={()=>openWA(s.whatsapp_number||s.contact_number,`Dear ${s.full_name},\nReminder from Armstech Engineers: ${s.dueItems.map(sc=>sc.label+' (due '+sc.due_date+')').join(', ')}. Amount: ₹${s.dueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0).toLocaleString('en-IN')}. Kindly pay on or before the due date.`)}>💬 Student</button>
                              {s.guardian_contact && <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'#128c7e',padding:'0 4px'}} onClick={()=>openWA(s.guardian_contact,`Dear Mr/Ms ${s.guardian_name||'Guardian'},\nReminder from Armstech Engineers regarding ${s.full_name}: ${s.dueItems.map(sc=>sc.label+' (due '+sc.due_date+')').join(', ')}. Amount: ₹${s.dueItems.reduce((x,sc)=>x+Number(sc.amount)-Number(sc.paid_amount||0),0).toLocaleString('en-IN')}. Kindly pay on or before the due date.`)}>💬 Guardian</button>}
                            </div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                </div>
              );
            }

            // ── INSTALLMENT DUE REPORT ──
            if (reportType==='installment_due') {
              const allInstNums = [...new Set(enriched.flatMap(s=>s.schedules.map(sc=>sc.installment_number)))].sort((a,b)=>a-b);
              return (
                <div className="card">
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:700,fontSize:14}}>📋 Installment Due Report — {batch}</div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{
                      const headers = allInstNums.map(n=>`<th>Inst ${n}</th>`).join('');
                      const rows = enriched.filter(s=>s.schedules.length>0).map((s,i)=>{
                        const cells = allInstNums.map(n=>{
                          const sc = s.schedules.find(x=>x.installment_number===n);
                          if (!sc) return '<td>—</td>';
                          const isOverdue = sc.status!=='paid'&&sc.due_date&&sc.due_date<today;
                          return `<td style="text-align:center;background:${sc.status==='paid'?'#dcfce7':isOverdue?'#fee2e2':'#fef9c3'};color:${sc.status==='paid'?'#15803d':isOverdue?'#dc2626':'#854d0e'};font-weight:700">${sc.status==='paid'?'✓ Paid':isOverdue?'Overdue':sc.due_date?fmtDate(sc.due_date):'Not set'}</td>`;
                        }).join('');
                        return `<tr><td>${i+1}</td><td>${esc(s.register_number)}</td><td>${esc(s.full_name)}</td><td>${esc(s.course_name)}</td>${cells}</tr>`;
                      }).join('');
                      printReport('Installment Due - '+batch,
                        `<h2>📋 Installment Due Report — ${esc(batch)}</h2>
                        <table><thead><tr><th>#</th><th>Reg No</th><th>Name</th><th>Course</th>${headers}</tr></thead>
                        <tbody>${rows}</tbody></table>`);
                    }}>🖨️ Print</button>
                  </div>
                  <div className="report-table-wrap"><table style={{width:'100%',borderCollapse:'separate',borderSpacing:0,fontSize:11}}>
                    <thead style={{position:'sticky',top:0,zIndex:10}}>
                      <tr>
                        {['#','Reg No','Name','Course',...allInstNums.map(n=>'Inst '+n)].map(h=>(
                          <th key={h} style={{padding:'6px 8px',background:'#e8f4fd',position:'sticky',top:0,textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {enriched.filter(s=>s.schedules.length>0).map((s,i)=>(
                        <tr key={s.id} style={{borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'5px 8px',color:'var(--text3)'}}>{i+1}</td>
                          <td style={{padding:'5px 8px',fontFamily:'monospace',fontSize:10}}>{s.register_number}</td>
                          <td style={{padding:'5px 8px',fontWeight:500,whiteSpace:'nowrap'}}>{s.full_name}</td>
                          <td style={{padding:'5px 8px',fontSize:10,color:'var(--text3)'}}>{s.course_name}</td>
                          {allInstNums.map(n=>{
                            const sc = s.schedules.find(x=>x.installment_number===n);
                            if (!sc) return <td key={n} style={{padding:'5px 8px',textAlign:'center',color:'#9ca3af'}}>—</td>;
                            const isOverdue = sc.status!=='paid'&&sc.status!=='waived'&&sc.due_date&&sc.due_date<today;
                            const bg = sc.status==='paid'?'#dcfce7':sc.status==='waived'?'#f3f4f6':isOverdue?'#fee2e2':sc.status==='partial'?'#fef9c3':'';
                            const col = sc.status==='paid'?'#15803d':isOverdue?'#dc2626':sc.status==='partial'?'#854d0e':'var(--text3)';
                            return <td key={n} style={{padding:'5px 8px',textAlign:'center',background:bg,color:col,fontWeight:600,fontSize:10,whiteSpace:'nowrap'}}>
                              {sc.status==='paid'?'✓':sc.status==='waived'?'W':sc.status==='partial'?'~'+fmtAmt(sc.paid_amount):isOverdue?'⚠️':sc.due_date?fmtDate(sc.due_date):'—'}
                            </td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                  {/* Summary row */}
                  <div style={{marginTop:12,padding:'8px 12px',background:'var(--surface2)',borderRadius:8,fontSize:12}}>
                    {allInstNums.map(n=>{
                      const all = enriched.flatMap(s=>s.schedules.filter(sc=>sc.installment_number===n));
                      const paid = all.filter(sc=>sc.status==='paid').length;
                      const overdue = all.filter(sc=>isOverdueSc(sc,today)).length;
                      const pending = all.length - paid - overdue;
                      return all.length>0 ? <span key={n} style={{marginRight:12}}>
                        <strong>Inst {n}:</strong> <span style={{color:'var(--green)'}}>✓{paid}</span> <span style={{color:'var(--red)'}}>⚠{overdue}</span> <span style={{color:'var(--text3)'}}>◌{pending}</span>
                      </span> : null;
                    })}
                  </div>
                </div>
              );
            }

            // ── COLLECTION REPORT ──
            if (reportType==='collection') {
              const filtered = enriched.map(s=>({
                ...s,
                filteredPayments: s.payments.filter(p=>
                  (!reportDateFrom||p.payment_date>=reportDateFrom) &&
                  (!reportDateTo||p.payment_date<=reportDateTo)
                )
              })).filter(s=>s.filteredPayments.length>0);
              const totalCollected = filtered.reduce((a,s)=>a+s.filteredPayments.reduce((b,p)=>b+Number(p.amount),0),0);
              return (
                <div className="card">
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:700,fontSize:14}}>💰 Collection Report — {batch}
                      <span style={{fontWeight:400,fontSize:12,color:'var(--text3)',marginLeft:8}}>
                        {reportDateFrom?fmtDate(reportDateFrom):''}{reportDateTo?' to '+fmtDate(reportDateTo):''}
                      </span>
                    </div>
                    <div style={{fontWeight:700,fontSize:15,color:'var(--green)'}}>Total: {fmtAmt(totalCollected)}</div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{
                      const rows = filtered.map((s,i)=>`<tr>
                        <td>${i+1}</td><td>${esc(s.register_number)}</td><td>${esc(s.full_name)}</td><td>${esc(s.course_name)}</td>
                        <td>${s.filteredPayments.map(p=>fmtDate(p.payment_date)+': ₹'+Number(p.amount).toLocaleString('en-IN')).join('<br/>')}</td>
                        <td style="text-align:right;font-weight:700">₹${s.filteredPayments.reduce((a,p)=>a+Number(p.amount),0).toLocaleString('en-IN')}</td>
                      </tr>`).join('');
                      printReport('Collection Report - '+batch,
                        `<h2>💰 Collection Report — ${esc(batch)}</h2><p>Total Collected: ₹${totalCollected.toLocaleString('en-IN')}</p>
                        <table><thead><tr><th>#</th><th>Reg No</th><th>Name</th><th>Course</th><th>Payments</th><th>Total</th></tr></thead>
                        <tbody>${rows}</tbody></table>`);
                    }}>🖨️ Print</button>
                  </div>
                  <div className="report-table-wrap"><table style={{width:'100%',fontSize:12}}>
                    <thead><tr>
                      {['#','Reg No','Name','Course','Payment Dates','Total Paid'].map(h=>(
                        <th key={h} style={{padding:'6px 10px',textAlign:'left'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {filtered.map((s,i)=>(
                        <tr key={s.id} style={{borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'6px 10px',color:'var(--text3)'}}>{i+1}</td>
                          <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11}}>{s.register_number}</td>
                          <td style={{padding:'6px 10px',fontWeight:500}}>{s.full_name}</td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.course_name}</td>
                          <td style={{padding:'6px 10px',fontSize:11}}>{s.filteredPayments.map((p,j)=>(
                            <span key={j} style={{marginRight:8}}>{fmtDate(p.payment_date)}: <strong>{fmtAmt(p.amount)}</strong></span>
                          ))}</td>
                          <td style={{padding:'6px 10px',fontWeight:700,color:'var(--green)'}}>
                            {fmtAmt(s.filteredPayments.reduce((a,p)=>a+Number(p.amount),0))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                </div>
              );
            }

            // ── FEE PENDING SUMMARY ──
            if (reportType==='pending_summary') {
              const byCourse = {};
              enriched.filter(s=>s.schedules.length>0).forEach(s=>{
                const key = s.course_name||'Unknown';
                if (!byCourse[key]) byCourse[key] = {students:0,totalFees:0,totalPaid:0};
                byCourse[key].students++;
                byCourse[key].totalFees += s.totalFees;
                byCourse[key].totalPaid += s.totalPaid;
              });
              const grandTotal = enriched.reduce((a,s)=>a+s.totalFees,0);
              const grandPaid = enriched.reduce((a,s)=>a+s.totalPaid,0);
              return (
                <div className="card">
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:700,fontSize:14}}>📊 Fee Pending Summary — {batch}</div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{
                      const rows = Object.entries(byCourse).map(([course,d])=>`<tr>
                        <td>${course}</td><td style="text-align:center">${d.students}</td>
                        <td style="text-align:right">₹${d.totalFees.toLocaleString('en-IN')}</td>
                        <td style="text-align:right;color:#15803d">₹${d.totalPaid.toLocaleString('en-IN')}</td>
                        <td style="text-align:right;color:#dc2626">₹${(d.totalFees-d.totalPaid).toLocaleString('en-IN')}</td>
                        <td style="text-align:center">${((d.totalPaid/d.totalFees)*100).toFixed(0)}%</td>
                      </tr>`).join('');
                      printReport('Fee Pending Summary - '+batch,
                        `<h2>📊 Fee Pending Summary — ${esc(batch)}</h2>
                        <table><thead><tr><th>Package</th><th>Students</th><th>Total Fees</th><th>Collected</th><th>Pending</th><th>%</th></tr></thead>
                        <tbody>${rows}<tr style="font-weight:700;background:#e8f4fd"><td>TOTAL</td><td style="text-align:center">${enriched.filter(s=>s.schedules.length>0).length}</td>
                        <td style="text-align:right">₹${grandTotal.toLocaleString('en-IN')}</td>
                        <td style="text-align:right;color:#15803d">₹${grandPaid.toLocaleString('en-IN')}</td>
                        <td style="text-align:right;color:#dc2626">₹${(grandTotal-grandPaid).toLocaleString('en-IN')}</td>
                        <td style="text-align:center">${((grandPaid/grandTotal)*100).toFixed(0)}%</td></tr></tbody></table>`);
                    }}>🖨️ Print</button>
                  </div>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                    <thead><tr style={{background:'var(--accent-light)'}}>
                      {['Package','Students','Total Fees','Collected','Pending','% Collected'].map(h=>(
                        <th key={h} style={{padding:'8px 12px',textAlign:h==='Package'||h==='Students'?'left':'right'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {Object.entries(byCourse).map(([course,d])=>(
                        <tr key={course} style={{borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'8px 12px',fontWeight:500}}>{course}</td>
                          <td style={{padding:'8px 12px',textAlign:'center'}}>{d.students}</td>
                          <td style={{padding:'8px 12px',textAlign:'right'}}>{fmtAmt(d.totalFees)}</td>
                          <td style={{padding:'8px 12px',textAlign:'right',color:'var(--green)',fontWeight:600}}>{fmtAmt(d.totalPaid)}</td>
                          <td style={{padding:'8px 12px',textAlign:'right',color:'var(--red)',fontWeight:600}}>{fmtAmt(d.totalFees-d.totalPaid)}</td>
                          <td style={{padding:'8px 12px',textAlign:'right'}}>
                            <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:8}}>
                              <div style={{width:60,height:6,background:'#e5e7eb',borderRadius:3,overflow:'hidden'}}>
                                <div style={{width:((d.totalPaid/d.totalFees)*100)+'%',height:'100%',background:'var(--green)',borderRadius:3}}/>
                              </div>
                              {((d.totalPaid/d.totalFees)*100).toFixed(0)}%
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{background:'var(--accent-light)',fontWeight:700}}>
                        <td style={{padding:'8px 12px'}}>TOTAL</td>
                        <td style={{padding:'8px 12px',textAlign:'center'}}>{enriched.filter(s=>s.schedules.length>0).length}</td>
                        <td style={{padding:'8px 12px',textAlign:'right'}}>{fmtAmt(grandTotal)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right',color:'var(--green)'}}>{fmtAmt(grandPaid)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right',color:'var(--red)'}}>{fmtAmt(grandTotal-grandPaid)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right'}}>{grandTotal>0?((grandPaid/grandTotal)*100).toFixed(0):0}%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            }

            // ── STUDENT SCHEDULE PRINT ──
            if (reportType==='student_schedule') {
              return (
                <div className="card">
                  <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>👤 Student Schedule Print — {batch}</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                    {enriched.filter(s=>s.schedules.length>0).map(s=>(
                      <button key={s.id} className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>{
                        const rows = s.schedules.map(sc=>`<tr>
                          <td>${sc.installment_number}</td><td>${esc(sc.label)}</td>
                          <td style="text-align:right">₹${Number(sc.amount).toLocaleString('en-IN')}</td>
                          <td>${sc.due_date?fmtDate(sc.due_date):'Not set'}</td>
                          <td style="text-align:right;color:#15803d">${sc.paid_amount>0?'₹'+Number(sc.paid_amount).toLocaleString('en-IN'):'—'}</td>
                          <td>${sc.payment_dates?.length>0?sc.payment_dates.map(d=>fmtDate(d)).join(', '):(sc.paid_date?fmtDate(sc.paid_date):'—')}</td>
                          <td><span class="${sc.status==='paid'?'badge-green':sc.status==='partial'?'badge-yellow':'badge-red'}">${sc.status}</span></td>
                        </tr>`).join('');
                        const totalFees = s.schedules.reduce((a,sc)=>a+Number(sc.amount),0);
                        const totalPaid = s.schedules.reduce((a,sc)=>a+Number(sc.paid_amount||0),0);
                        printReport('Fee Schedule - '+s.full_name,
                          `<h2>Fee Schedule — ${esc(s.full_name)}</h2>
                          <p>Reg No: ${esc(s.register_number)} | Batch: ${batch} | Course: ${esc(s.course_name)}</p>
                          <p>Total Fees: ₹${totalFees.toLocaleString('en-IN')} | Paid: ₹${totalPaid.toLocaleString('en-IN')} | Balance: ₹${(totalFees-totalPaid).toLocaleString('en-IN')}</p>
                          <table><thead><tr><th>#</th><th>Installment</th><th>Amount</th><th>Due Date</th><th>Paid</th><th>Paid Date(s)</th><th>Status</th></tr></thead>
                          <tbody>${rows}</tbody></table>`);
                      }}>{s.register_number} — {s.full_name}</button>
                    ))}
                  </div>
                </div>
              );
            }

            // ── BATCH FEE TRACKING ──
            if (reportType==='batch_tracking') {
              const grandTotal = enriched.reduce((a,s)=>a+s.totalFees,0);
              const grandPaid = enriched.reduce((a,s)=>a+s.totalPaid,0);
              return (
                <div className="card">
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:700,fontSize:14}}>📦 Batch Fee Tracking — {batch}</div>
                    <div style={{display:'flex',gap:16,fontSize:13}}>
                      <span>Students: <strong>{enriched.filter(s=>s.schedules.length>0).length}</strong></span>
                      <span style={{color:'var(--green)'}}>Collected: <strong>{fmtAmt(grandPaid)}</strong></span>
                      <span style={{color:'var(--red)'}}>Pending: <strong>{fmtAmt(grandTotal-grandPaid)}</strong></span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>exportRows('Batch_Tracking_'+batch, enriched.filter(s=>s.schedules.length>0).map((s,i)=>({
                      'S.No':i+1,'Batch':s.batch_name,'Reg No':s.register_number,'Name':s.full_name,'Course':s.course_name,
                      'Total Fees':s.totalFees,'Paid (schedule)':s.totalPaid,'Payments received (net)':s.netReceived,'Balance':s.totalFees-s.totalPaid})))}>⬇ Excel</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{
                      const rows = enriched.filter(s=>s.schedules.length>0).map((s,i)=>`<tr>
                        <td>${i+1}</td><td>${esc(s.register_number)}</td><td>${esc(s.full_name)}</td><td>${esc(s.course_name)}</td>
                        <td style="text-align:right">₹${s.totalFees.toLocaleString('en-IN')}</td>
                        <td style="text-align:right;color:#15803d">₹${s.totalPaid.toLocaleString('en-IN')}</td>
                        <td style="text-align:right;color:#dc2626">₹${(s.totalFees-s.totalPaid).toLocaleString('en-IN')}</td>
                        <td style="text-align:center">${s.totalFees>0?((s.totalPaid/s.totalFees)*100).toFixed(0):0}%</td>
                        <td>${s.schedules.filter(sc=>sc.status!=='paid'&&sc.status!=='waived'&&sc.due_date&&sc.due_date<today).length>0?'⚠️ Overdue':s.schedules.filter(sc=>sc.status==='paid').length===s.schedules.length?'✓ Complete':'Active'}</td>
                      </tr>`).join('');
                      printReport('Batch Fee Tracking - '+batch,
                        `<h2>📦 Batch Fee Tracking — ${esc(batch)}</h2>
                        <p>Total Collected: ₹${grandPaid.toLocaleString('en-IN')} | Pending: ₹${(grandTotal-grandPaid).toLocaleString('en-IN')}</p>
                        <table><thead><tr><th>#</th><th>Reg No</th><th>Name</th><th>Course</th><th>Total Fees</th><th>Paid</th><th>Balance</th><th>%</th><th>Status</th></tr></thead>
                        <tbody>${rows}</tbody></table>`);
                    }}>🖨️ Print</button>
                  </div>
                  <div className="report-table-wrap"><table style={{width:'100%',fontSize:12}}>
                    <thead><tr>
                      {['#','Reg No','Name','Course','Total Fees','Paid','Balance','%','Status'].map(h=>(
                        <th key={h} style={{padding:'6px 10px',textAlign:['Total Fees','Paid','Balance','%'].includes(h)?'right':'left'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {enriched.filter(s=>s.schedules.length>0).map((s,i)=>{
                        const isOverdue = s.schedules.some(sc=>isOverdueSc(sc,today));
                        const isComplete = s.schedules.every(sc=>sc.status==='paid'||sc.status==='waived');
                        const pct = s.totalFees>0?(s.totalPaid/s.totalFees*100).toFixed(0):0;
                        return (
                          <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:isOverdue?'#fef2f2':isComplete?'#f0fdf4':''}}>
                            <td style={{padding:'6px 10px',color:'var(--text3)'}}>{i+1}</td>
                            <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11}}>{s.register_number}</td>
                            <td style={{padding:'6px 10px',fontWeight:500}}>{s.full_name}</td>
                            <td style={{padding:'6px 10px',fontSize:11,color:'var(--text3)'}}>{s.course_name}</td>
                            <td style={{padding:'6px 10px',textAlign:'right'}}>{fmtAmt(s.totalFees)}</td>
                            <td style={{padding:'6px 10px',textAlign:'right',color:'var(--green)',fontWeight:600}}>{fmtAmt(s.totalPaid)}
                              {Math.abs(s.netReceived - s.totalPaid) > 1 && <div title={'Payments received (net of refunds): '+fmtAmt(s.netReceived)+' — differs from what is allocated to the installments. Re-save a payment or regenerate to re-allocate.'} style={{fontSize:9,color:'var(--amber)',fontWeight:600}}>⚠ received {fmtAmt(s.netReceived)}</div>}
                            </td>
                            <td style={{padding:'6px 10px',textAlign:'right',color:s.totalFees-s.totalPaid>0?'var(--red)':'var(--green)',fontWeight:600}}>{fmtAmt(s.totalFees-s.totalPaid)}</td>
                            <td style={{padding:'6px 10px',textAlign:'right'}}>
                              <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:6}}>
                                <div style={{width:40,height:4,background:'#e5e7eb',borderRadius:2,overflow:'hidden'}}>
                                  <div style={{width:pct+'%',height:'100%',background:isComplete?'var(--green)':isOverdue?'var(--red)':'var(--amber)',borderRadius:2}}/>
                                </div>
                                {pct}%
                              </div>
                            </td>
                            <td style={{padding:'6px 10px'}}>
                              {isComplete?<span className="badge badge-green">Complete</span>
                                :isOverdue?<span className="badge badge-red">⚠️ Overdue</span>
                                :<span className="badge badge-yellow">Active</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table></div>
                </div>
              );
            }


            // -- PAYMENT MILESTONE REPORT --
            if (reportType==='milestone') {
              const feeStudents = enriched.filter(s=>s.schedules.length>0);
              // Determine milestones based on actual paid amounts
              const paidAmounts = feeStudents.map(s=>s.totalPaid).filter(a=>a>0).sort((a,b)=>a-b);
              // Generate milestone thresholds: every 5000 up to max paid
              const maxPaid = paidAmounts.length ? Math.max(...paidAmounts) : 0;
              const step = 5000;
              const milestones = [];
              for (let m = step; m <= maxPaid + step; m += step) {
                milestones.push(m);
              }
              return (
                <div className="card">
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:700,fontSize:14}}>&#127942; Payment Milestone Report &mdash; {batch}</div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{
                      const rows = milestones.map(m=>{
                        const reached = feeStudents.filter(s=>s.totalPaid>=m);
                        const pct = feeStudents.length>0?(reached.length/feeStudents.length*100).toFixed(0):0;
                        return `<tr><td style="text-align:right;font-weight:700">&#8377;${m.toLocaleString('en-IN')}</td><td style="text-align:center;font-weight:700;color:#15803d">${reached.length}</td><td style="text-align:center;color:#6b7280">${feeStudents.length-reached.length}</td><td style="text-align:center;font-weight:700">${pct}%</td><td>${reached.map(s=>s.register_number+' '+s.full_name).join(', ')}</td></tr>`;
                      }).join('');
                      printReport('Payment Milestone - '+batch,
                        `<h2>&#127942; Payment Milestone Report &mdash; ${esc(batch)}</h2>
                        <p>Total fee-paying students: ${feeStudents.length}</p>
                        <table><thead><tr><th>Milestone</th><th>Reached</th><th>Not Reached</th><th>%</th><th>Students</th></tr></thead>
                        <tbody>${rows}</tbody></table>`);
                    }}>&#128424; Print</button>
                  </div>
                  <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                    <thead>
                      <tr style={{background:'var(--accent-light)'}}>
                        {['Milestone Amount','Reached','Not Reached','% Reached',''].map(h=>(
                          <th key={h} style={{padding:'8px 10px',textAlign:h==='Milestone Amount'||h===''?'left':'center'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {milestones.map(m=>{
                        const reached = feeStudents.filter(s=>s.totalPaid>=m);
                        const notReached = feeStudents.filter(s=>s.totalPaid<m);
                        const pct = feeStudents.length>0?(reached.length/feeStudents.length*100).toFixed(0):0;
                        return (
                          <tr key={m} style={{borderBottom:'1px solid var(--border)',background:reached.length===feeStudents.length?'rgba(0,176,155,0.08)':''}}>
                            <td style={{padding:'8px 10px',fontWeight:700,color:'var(--accent)'}}>
                              {fmtAmt(m)}
                            </td>
                            <td style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:'var(--green)',fontSize:16}}>
                              {reached.length}
                            </td>
                            <td style={{padding:'8px 10px',textAlign:'center',color:'var(--red)',fontWeight:600}}>
                              {notReached.length}
                            </td>
                            <td style={{padding:'8px 10px',textAlign:'center'}}>
                              <div style={{display:'flex',alignItems:'center',gap:8,justifyContent:'center'}}>
                                <div style={{width:80,height:6,background:'var(--border)',borderRadius:3}}>
                                  <div style={{width:pct+'%',height:'100%',background:'var(--green)',borderRadius:3}}/>
                                </div>
                                <span style={{fontWeight:700}}>{pct}%</span>
                              </div>
                            </td>
                            <td style={{padding:'8px 10px',fontSize:11,color:'var(--accent)',cursor:'pointer',textDecoration:'underline'}}
                              onClick={()=>setSelMilestone({amount:m,reached,notReached})}>
                              View {reached.length} students
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{marginTop:12,fontSize:12,color:'var(--text3)'}}>
                    Total fee-paying students: <strong>{feeStudents.length}</strong> &nbsp;|&nbsp;
                    Avg paid: <strong>{fmtAmt(feeStudents.length?Math.round(feeStudents.reduce((a,s)=>a+s.totalPaid,0)/feeStudents.length):0)}</strong>
                  </div>
                  {/* Milestone Student Modal */}
                  {selMilestone && (
                    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSelMilestone(null)}>
                      <div className="modal" style={{maxWidth:700}}>
                        <div className="modal-header">
                          <span className="modal-title">Students who paid {fmtAmt(selMilestone.amount)} or more</span>
                          <button className="btn btn-ghost btn-sm" onClick={()=>setSelMilestone(null)}>x</button>
                        </div>
                        <div className="modal-body" style={{maxHeight:'60vh',overflowY:'auto'}}>
                          <div style={{display:'flex',gap:12,marginBottom:12}}>
                            <span style={{padding:'4px 12px',borderRadius:8,background:'var(--green-light)',color:'var(--green)',fontWeight:600}}>
                              Reached: {selMilestone.reached.length}
                            </span>
                            <span style={{padding:'4px 12px',borderRadius:8,background:'var(--red-light)',color:'var(--red)',fontWeight:600}}>
                              Not Reached: {selMilestone.notReached.length}
                            </span>
                          </div>
                          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                            <thead>
                              <tr style={{background:'var(--green-light)'}}>
                                <th style={{padding:'6px 10px',textAlign:'left'}}>#</th>
                                <th style={{padding:'6px 10px',textAlign:'left'}}>Reg No</th>
                                <th style={{padding:'6px 10px',textAlign:'left'}}>Name</th>
                                <th style={{padding:'6px 10px',textAlign:'left'}}>Course</th>
                                <th style={{padding:'6px 10px',textAlign:'right'}}>Total Paid</th>
                                <th style={{padding:'6px 10px',textAlign:'right'}}>Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selMilestone.reached.map((s,i)=>(
                                <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                                  <td style={{padding:'6px 10px',color:'var(--text3)'}}>{i+1}</td>
                                  <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11,color:'var(--accent)'}}>{s.register_number}</td>
                                  <td style={{padding:'6px 10px',fontWeight:500}}>{s.full_name}</td>
                                  <td style={{padding:'6px 10px',fontSize:11,color:'var(--text3)'}}>{s.course_name}</td>
                                  <td style={{padding:'6px 10px',textAlign:'right',color:'var(--green)',fontWeight:600}}>{fmtAmt(s.totalPaid)}</td>
                                  <td style={{padding:'6px 10px',textAlign:'right',color:s.totalFees-s.totalPaid>0?'var(--red)':'var(--green)',fontWeight:600}}>{fmtAmt(s.totalFees-s.totalPaid)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="modal-footer">
                          <button className="btn btn-primary btn-sm" onClick={()=>{
                            const rows = selMilestone.reached.map((s,i)=>`<tr>
                              <td>${i+1}</td><td>${esc(s.register_number)}</td><td>${esc(s.full_name)}</td>
                              <td>${esc(s.course_name)}</td>
                              <td style="text-align:right;color:#15803d;font-weight:700">&#8377;${s.totalPaid.toLocaleString('en-IN')}</td>
                              <td style="text-align:right;color:#dc2626;font-weight:700">&#8377;${(s.totalFees-s.totalPaid).toLocaleString('en-IN')}</td>
                            </tr>`).join('');
                            printReport('Milestone '+fmtAmt(selMilestone.amount)+' - '+batch,
                              `<h2>Students who paid ${fmtAmt(selMilestone.amount)} or more &mdash; ${esc(batch)}</h2>
                              <p>Total: ${selMilestone.reached.length} students | Generated: ${new Date().toLocaleDateString('en-IN')}</p>
                              <table><thead><tr><th>#</th><th>Reg No</th><th>Name</th><th>Course</th><th>Total Paid</th><th>Balance</th></tr></thead>
                              <tbody>${rows}</tbody></table>`);
                          }}>Print PDF</button>
                          <button className="btn btn-secondary btn-sm" onClick={()=>setSelMilestone(null)}>Close</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* ── EDIT SCHEDULE MODAL ── */}
      {editModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setEditModal(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header">
              <span className="modal-title">Edit Installment — {editModal.label}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setEditModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Due Date</label>
                <DateInput value={editModal.due_date||''} onChange={e=>setEditModal({...editModal,due_date:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={editModal.status} onChange={e=>setEditModal({...editModal,status:e.target.value})}>
                  {['pending','paid','partial','waived'].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Amount Due</label>
                <input type="number" value={editModal.amount} onChange={e=>setEditModal({...editModal,amount:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Amount Paid</label>
                <input type="number" value={editModal.paid_amount||''} onChange={e=>setEditModal({...editModal,paid_amount:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Paid Date</label>
                <DateInput value={editModal.paid_date||''} onChange={e=>setEditModal({...editModal,paid_date:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Late Fee</label>
                <input type="number" value={editModal.late_fee||''} onChange={e=>setEditModal({...editModal,late_fee:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={editModal.notes||''} onChange={e=>setEditModal({...editModal,notes:e.target.value})} rows={2}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>saveScheduleRow(editModal)}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── REVERT DATE MODAL ── */}
      {revertModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setRevertModal(null)}>
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-header">
              <span className="modal-title">↩ Change Due Date</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setRevertModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{fontSize:13,marginBottom:12,color:'var(--text2)'}}>
                Original date: <strong>{fmtDate(revertModal.original_due_date)}</strong><br/>
                Extended to: <strong style={{color:'var(--amber)'}}>{fmtDate(revertModal.requested_date)}</strong>
              </p>
              <div className="form-group">
                <label>Set New Due Date</label>
                <DateInput value={revertDate} onChange={e=>setRevertDate(e.target.value)} />
              </div>
              <div style={{display:'flex',gap:8,marginTop:8}}>
                <button className="btn btn-ghost btn-sm" style={{fontSize:11}}
                  onClick={()=>setRevertDate(revertModal.original_due_date||'')}>
                  Use Original ({fmtDate(revertModal.original_due_date)})
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setRevertModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!revertDate} onClick={confirmRevert}>
                ✓ Update Due Date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXTENSION REQUEST MODAL ── */}
      {extModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setExtModal(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header">
              <span className="modal-title">🗓 Request Extension — {extModal.label}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setExtModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{marginBottom:12,fontSize:13,color:'var(--text2)'}}>
                Current due date: <strong>{extModal.due_date?fmtDate(extModal.due_date):'Not set'}</strong>
              </div>
              <div className="form-group">
                <label>New Requested Date *</label>
                <DateInput value={extForm.requested_date}
                  onChange={e=>setExtForm({...extForm,requested_date:e.target.value})}
                  />
              </div>
              <div className="form-group">
                <label>Reason</label>
                <textarea value={extForm.reason} rows={3}
                  onChange={e=>setExtForm({...extForm,reason:e.target.value})}
                  placeholder="Reason for requesting extension..."/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setExtModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!extForm.requested_date||extSaving}
                onClick={saveExtensionRequest}>
                {extSaving?'Submitting...':'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SHIFT DUE DATES MODAL ── */}
      {shiftModal && (()=>{
        const batchRow = batches.find(b=>b.batch_name===selBatch);
        const preview = shiftPreview();
        return (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShiftModal(null)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-header">
              <span className="modal-title">📅 Shift due dates — {selBatch}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShiftModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>Moves the due dates of all <strong>unpaid</strong> installments in this batch. Paid and waived installments are not touched.</p>
              <div className="form-group">
                <label>Batch start date was moved from (optional helper)</label>
                <DateInput value={shiftModal.oldStart} onChange={e=>{
                  const old=e.target.value;
                  const diff = old && batchRow?.start_date ? Math.round((new Date(batchRow.start_date)-new Date(old))/86400000) : '';
                  setShiftModal({...shiftModal,oldStart:old,days:diff!==''?String(diff):shiftModal.days});
                }}/>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>Current batch start date: <strong>{batchRow?.start_date?fmtDate(batchRow.start_date):'not set'}</strong></div>
              </div>
              <div className="form-group">
                <label>Shift by (days, negative = earlier)</label>
                <input type="number" value={shiftModal.days} onChange={e=>setShiftModal({...shiftModal,days:e.target.value})} placeholder="e.g. 14"/>
              </div>
              <div className="form-group">
                <label>Starting from installment #</label>
                <input type="number" min="1" value={shiftModal.fromInst} onChange={e=>setShiftModal({...shiftModal,fromInst:e.target.value})}/>
              </div>
              <div style={{fontSize:12,padding:'8px 10px',background:'var(--surface2)',borderRadius:6}}>
                {preview.length ? <>Will move <strong>{preview.length}</strong> due dates. Example: {fmtDate(preview[0].due_date)} → <strong>{fmtDate(addDays(preview[0].due_date,Number(shiftModal.days)||0))}</strong></> : 'Enter the number of days to see how many dates will move.'}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShiftModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!preview.length||shifting} onClick={runShift}>{shifting?'Moving...':'Move dates'}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── COPY PLAN MODAL ── */}
      {copyModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setCopyModal(null)}>
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-header">
              <span className="modal-title">⧉ Copy plan to a new year</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setCopyModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Copy from year</label>
                <select value={copyModal.fromYear} onChange={e=>setCopyModal({...copyModal,fromYear:Number(e.target.value)})}>
                  {[...new Set(pkgPlans.map(p=>Number(p.valid_from_year)))].sort().map(y=><option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>New plan valid from year</label>
                <input type="number" value={copyModal.toYear} onChange={e=>setCopyModal({...copyModal,toYear:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Fee change % (optional, e.g. 10 for +10%)</label>
                <input type="number" value={copyModal.pct} onChange={e=>setCopyModal({...copyModal,pct:e.target.value})} placeholder="0"/>
              </div>
              <div style={{fontSize:11,color:'var(--text3)'}}>Batches that start in the new year (or later) will use the new plan; older batches keep theirs. Existing schedules are not changed.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setCopyModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={runCopyPlan}>Copy</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT PLAN MODAL ── */}
      {planForm && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setPlanForm(null)}>
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-header">
              <span className="modal-title">{planForm.id?'Edit':'Add'} Installment</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setPlanForm(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Installment Number</label>
                <input type="number" value={planForm.installment_number} onChange={e=>setPlanForm({...planForm,installment_number:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Label</label>
                <input value={planForm.label} onChange={e=>setPlanForm({...planForm,label:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Amount (₹)</label>
                <input type="number" value={planForm.amount} onChange={e=>setPlanForm({...planForm,amount:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={planForm.type} onChange={e=>setPlanForm({...planForm,type:e.target.value})}>
                  {['registration','monthly','exam','certificate'].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Valid From Year</label>
                <input type="number" value={planForm.valid_from_year} onChange={e=>setPlanForm({...planForm,valid_from_year:e.target.value})}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setPlanForm(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={planSaving} onClick={()=>savePlan(planForm)}>
                {planSaving?'Saving...':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
